// ===============================================================
// ProBank — POS Terminal Payment
//
// Pay a POS agent's bank account. Simple flow: account number,
// bank, amount. The verified account name is used automatically
// as the recipient name — no manual agent name field.
//
// ORDER OF CHECKS (like real banks):
//   1. Insufficient balance → ALWAYS shown first, before anything
//   2. Delivery gate (no subscription + no card) → warning, only
//      when the user can actually afford the payment
//   3. PIN → payment
// ===============================================================

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { useCountry } from "../context/CountryContext";
import PinPad from "../components/PinPad";
import { fetchDeliveryGate, DeliveryGateModal } from "../components/DeliveryGate";

const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

// 🎨 Per-wallet brand skins (hex values for inline styles)
const BRAND = {
  opay: {
    label: "ProBank Wallet", emoji: "💚",
    bg: "#04100a", card: "#0a1f14", line: "#13402850",
    accent: "#34d399", accentSoft: "#34d39914", accentText: "#04100a",
  },
  palmpay: {
    label: "PalmPay", emoji: "💜",
    bg: "#0d0514", card: "#1a0f26", line: "#6d28d950",
    accent: "#c084fc", accentSoft: "#c084fc14", accentText: "#1a0f26",
  },
  kuda: {
    label: "Kuda", emoji: "💜",
    bg: "#0b0713", card: "#170f26", line: "#8b5cf650",
    accent: "#a78bfa", accentSoft: "#a78bfa14", accentText: "#170f26",
  },
  moniepoint: {
    label: "Moniepoint", emoji: "💙",
    bg: "#040d16", card: "#0a1a2a", line: "#38bdf850",
    accent: "#38bdf8", accentSoft: "#38bdf814", accentText: "#040d16",
  },
};

export default function POSTransferPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGhana, config } = useCountry();

  const cur = config?.currencySymbol || (isGhana ? "GH₵" : "₦");
  const countryCode = isGhana ? "GH" : "NG";
  const brand = BRAND[location.state?.from] || BRAND.opay;

  const [user, setUser] = useState(null);
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [resolvedName, setResolvedName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [amount, setAmount] = useState("");
  const [recents, setRecents] = useState([]);
  const [stage, setStage] = useState("form"); // form | confirm | processing | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [blocker, setBlocker] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);

  // Delivery gate — checked on mount, enforced before PIN
  const [canDeliver, setCanDeliver] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem("user") || "{}"));
    } catch {}
  }, []);

  // Check upfront whether this user's transfers reach recipients
  useEffect(() => {
    fetchDeliveryGate().then(({ canDeliver }) => setCanDeliver(canDeliver));
  }, []);

  // Recent POS recipients
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE}/bank/recipients?type=pos`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setRecents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Real bank list for the user's country
  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true);
      try {
        const res = await fetch(`${API_BASE}/bank/list?country=${countryCode}`);
        const data = await res.json();
        setBanks(
          (Array.isArray(data) ? data : []).map((b) => ({
            value: b.code,
            label: b.name,
          }))
        );
      } catch {
        setBanks([]);
      } finally {
        setBanksLoading(false);
      }
    };
    fetchBanks();
  }, [countryCode]);

  // Live account-name verification
  useEffect(() => {
    const resolveAccount = async () => {
      if (accountNumber.length === 10 && selectedBank?.value) {
        setResolving(true);
        setResolvedName("");
        try {
          const res = await fetch(`${API_BASE}/bank/resolve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              account_number: accountNumber,
              bank_code: selectedBank.value,
              country: countryCode,
            }),
          });
          const data = await res.json();
          const name = data?.account_name || data?.data?.account_name;
          setResolvedName(name || "❌ Could not verify account");
        } catch {
          setResolvedName("⚠️ Error verifying account");
        } finally {
          setResolving(false);
        }
      } else {
        setResolvedName("");
      }
    };
    resolveAccount();
  }, [accountNumber, selectedBank, countryCode]);

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const nameOk = resolvedName && !resolvedName.startsWith("❌") && !resolvedName.startsWith("⚠️");

  const pickRecent = (r) => {
    setAccountNumber(r.accountNumber || "");
    if (r.bankCode) {
      const match = banks.find((b) => b.value === r.bankCode);
      if (match) setSelectedBank(match);
    }
  };

  // Balance check — ALWAYS evaluated before any gate warning
  const hasInsufficientFunds = () => Number(amount) > Number(user?.balance || 0);

  const insufficientMessage = () =>
    `This payment of ${cur}${fmt(amount)} is more than your available balance of ${cur}${fmt(user?.balance)}. Please fund your wallet first.`;

  const validate = () => {
    if (!selectedBank) return "Select the terminal's bank.";
    if (accountNumber.length !== 10) return "Enter a valid 10-digit account number.";
    if (!nameOk) return "The account name could not be verified.";
    if (!Number(amount) || Number(amount) <= 0) return "Enter a valid amount.";
    return null;
  };

  const handleSubmit = () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");

    // Balance check first — before the confirm screen even opens
    if (hasInsufficientFunds()) {
      setBlocker({
        icon: "💳",
        title: "Insufficient Balance",
        message: insufficientMessage(),
        action: { label: "Fund Wallet", path: "/purchase" },
      });
      return;
    }

    setStage("confirm");
  };

  // Called by PinPad with the entered 4-digit PIN
  const submitPayment = async (pin) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${API_BASE}/bank/wallet/pos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          amount: Number(amount),
          account_number: accountNumber,
          bank_code: selectedBank.value,
          bank_name: selectedBank.label,
          account_name: resolvedName,
          terminal_name: resolvedName,
          pin,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (["PIN_INCORRECT", "PIN_LOCKED", "PIN_NOT_SET", "PIN_REQUIRED"].includes(data.code)) {
          return { ok: false, data };
        }

        if (data.code === "ACCOUNT_LOCKED") {
          setBlocker({ icon: "🔒", title: "Account Locked", message: data.error });
        } else if (data.code === "INSUFFICIENT_BALANCE") {
          setBlocker({ icon: "💳", title: "Insufficient Balance", message: data.error, action: { label: "Purchase Coins", path: "/purchase" } });
        } else {
          setError(data.error || "Transaction failed.");
        }
        setStage("form");
        return { ok: false, data };
      }

      const tx = data.transaction || {};
      tx.delivered = data.delivered !== false;
      setResult(tx);

      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        u.balance = tx.new_balance;
        localStorage.setItem("user", JSON.stringify(u));
        setUser(u);
      } catch {}

      setStage("processing");
      setTimeout(() => setStage("done"), 2200);
      return { ok: true, data };
    } catch {
      setError("Network error. Please try again.");
      setStage("form");
      return { ok: false, data: {} };
    }
  };

  // Confirm & Pay → balance first, then gate, then PIN
  const handleConfirm = () => {
    // Balance check ALWAYS comes first — before the gate warning
    if (hasInsufficientFunds()) {
      setBlocker({
        icon: "💳",
        title: "Insufficient Balance",
        message: insufficientMessage(),
        action: { label: "Fund Wallet", path: "/purchase" },
      });
      return;
    }

    // Gate only when funds are sufficient
    if (!canDeliver) {
      setGateOpen(true);
      return;
    }

    setPinOpen(true);
  };

  const inputStyle = {
    width: "100%", padding: "13px 14px", borderRadius: 10,
    border: `1px solid ${brand.line}`, background: brand.bg,
    color: "#f4f6f8", fontSize: 14, outline: "none", boxSizing: "border-box",
  };

  const labelStyle = { display: "block", fontSize: 12, color: "#7a93ab", marginBottom: 6, fontWeight: 600 };

  const selectStyles = {
    control: (base) => ({
      ...base, backgroundColor: brand.bg, border: `1px solid ${brand.line}`,
      borderRadius: 10, padding: "3px", boxShadow: "none",
    }),
    singleValue: (base) => ({ ...base, color: "#f4f6f8" }),
    input: (base) => ({ ...base, color: "#f4f6f8" }),
    menu: (base) => ({ ...base, backgroundColor: brand.card, zIndex: 30 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? brand.accentSoft : brand.card,
      color: "#f4f6f8",
    }),
    placeholder: (base) => ({ ...base, color: "#7a93ab" }),
  };

  return (
    <div style={{ minHeight: "100vh", background: brand.bg, color: "#f4f6f8", fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${brand.line}`, position: "sticky", top: 0, background: brand.bg, zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: "#f4f6f8", fontSize: 20, cursor: "pointer", padding: 4 }}>←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>POS Payment</div>
          <div style={{ fontSize: 11, color: "#7a93ab" }}>Available balance: {cur}{fmt(user?.balance)}</div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: brand.accent, fontWeight: 600 }}>
          {brand.emoji} {brand.label}
        </div>
      </div>

      <div style={{ maxWidth: 460, margin: "0 auto", padding: "20px 20px 60px" }}>
        {stage === "form" && (
          <>
            {recents.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, color: "#7a93ab", fontWeight: 600, marginBottom: 10 }}>RECENT TERMINALS</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {recents.map((r, i) => (
                    <button key={i} onClick={() => pickRecent(r)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 10, cursor: "pointer", textAlign: "left" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: brand.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>📟</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: "#f4f6f8", fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                        <div style={{ color: "#7a93ab", fontSize: 11 }}>{r.accountNumber}{r.bankName ? ` · ${r.bankName}` : ""}</div>
                      </div>
                      <span style={{ color: "#7a93ab", fontSize: 14 }}>›</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>TERMINAL'S BANK</label>
              <Select
                options={banks}
                value={selectedBank}
                onChange={setSelectedBank}
                isSearchable
                isLoading={banksLoading}
                placeholder={banksLoading ? "Loading banks..." : "Search banks..."}
                styles={selectStyles}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>ACCOUNT NUMBER</label>
              <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit account number" inputMode="numeric" style={inputStyle} />
              {resolving && (
                <div style={{ color: brand.accent, fontSize: 12, marginTop: 6 }}>🔄 Verifying account...</div>
              )}
              {resolvedName && !resolving && (
                <div style={{
                  marginTop: 6, fontSize: 12, padding: "7px 12px", borderRadius: 8,
                  background: nameOk ? "#22c55e1a" : "#ef44441a",
                  color: nameOk ? "#4ade80" : "#f87171",
                  border: `1px solid ${nameOk ? "#22c55e40" : "#ef444440"}`,
                }}>
                  {nameOk ? `👤 ${resolvedName}` : resolvedName}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>AMOUNT ({cur})</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0.00" inputMode="decimal" style={{ ...inputStyle, fontSize: 18, fontWeight: 700 }} />
            </div>

            {error && <div style={{ color: "#f87171", fontSize: 13, margin: "10px 0" }}>{error}</div>}

            <button onClick={handleSubmit}
              style={{ width: "100%", marginTop: 14, padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
              Review Payment
            </button>
          </>
        )}

        {stage === "confirm" && (
          <div style={{ background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${brand.line}` }}>
              <div style={{ fontSize: 12, color: "#7a93ab", fontWeight: 600 }}>CONFIRM PAYMENT</div>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ fontSize: 11, color: "#7a93ab", marginBottom: 4 }}>You are paying</div>
                <div style={{ fontSize: 32, fontWeight: 800 }}>{cur}{fmt(amount)}</div>
              </div>
              {[
                ["Account Name", resolvedName],
                ["Bank", selectedBank?.label || ""],
                ["Account Number", accountNumber],
                ["Channel", "POS Terminal"],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${brand.line}` }}>
                  <span style={{ color: "#7a93ab", fontSize: 13 }}>{k}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>{v}</span>
                </div>
              ))}
              <button onClick={handleConfirm}
                style={{ width: "100%", marginTop: 20, padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
                Confirm &amp; Pay
              </button>
              <button onClick={() => setStage("form")}
                style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 10, border: `1px solid ${brand.line}`, background: "none", color: "#7a93ab", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Back
              </button>
            </div>
          </div>
        )}

        {stage === "processing" && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ width: 40, height: 40, margin: "0 auto 18px", border: `3px solid ${brand.line}`, borderTopColor: brand.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Processing payment…</div>
            <div style={{ fontSize: 12, color: "#7a93ab", marginTop: 6 }}>Contacting the terminal network. Do not close this page.</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {stage === "done" && result && (
          <div>
            <div style={{ background: "#fff", color: "#111", borderRadius: 12, padding: "24px 22px", fontFamily: "'Courier New', monospace", boxShadow: "0 8px 30px rgba(0,0,0,0.4)" }}>
              <div style={{ textAlign: "center", borderBottom: "1px dashed #999", paddingBottom: 14, marginBottom: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>PROBANK POS</div>
                <div style={{ fontSize: 11, color: "#555" }}>TERMINAL PAYMENT RECEIPT</div>
                <div style={{
                  marginTop: 10, display: "inline-block", padding: "3px 14px",
                  background: result.delivered ? "#dcfce7" : "#fef3c7",
                  color: result.delivered ? "#166534" : "#92400e",
                  borderRadius: 4, fontSize: 12, fontWeight: 800, letterSpacing: 2,
                }}>
                  {result.delivered ? "APPROVED" : "PENDING"}
                </div>
              </div>
              {[
                ["NAME", (resolvedName || "").toUpperCase()],
                ["BANK", (selectedBank?.label || "").toUpperCase()],
                ["ACCOUNT", result.recipientAccount || accountNumber],
                ["AMOUNT", `${cur}${fmt(result.amount)}`],
                ["REF", result.transactionId || ""],
                ["DATE", new Date().toLocaleString()],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0" }}>
                  <span style={{ color: "#666" }}>{k}</span>
                  <span style={{ fontWeight: 700, wordBreak: "break-all", textAlign: "right", maxWidth: "60%" }}>{v}</span>
                </div>
              ))}
              <div style={{ borderTop: "1px dashed #999", marginTop: 14, paddingTop: 12, textAlign: "center", fontSize: 11, color: "#666" }}>
                Thank you for using ProBank
              </div>
            </div>

            {!result.delivered && (
              <div style={{
                marginTop: 16, padding: "14px 16px", borderRadius: 10,
                background: "#f59e0b1a", border: "1px solid #f59e0b40",
                color: "#fbbf24", fontSize: 12, lineHeight: 1.6,
              }}>
                This transaction is recorded but will not reflect in the recipient's
                account until you subscribe to a plan or activate a ProBank card.
              </div>
            )}

            <button onClick={() => navigate("/dashboard")}
              style={{ width: "100%", marginTop: 20, padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
              Back to Dashboard
            </button>
          </div>
        )}
      </div>

      {/* Pre-transfer delivery warning (only when gated) */}
      <DeliveryGateModal
        open={gateOpen}
        brand={brand}
        onContinue={() => {
          setGateOpen(false);
          setPinOpen(true);
        }}
        onCancel={() => setGateOpen(false)}
      />

      {/* Transaction PIN pad */}
      <PinPad
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSuccess={submitPayment}
        title="Authorize POS Payment"
        subtitle={`${cur}${fmt(amount)} to ${resolvedName || "terminal"}`}
        brand={{ accent: brand.accent, accentText: brand.accentText }}
      />

      {/* Blocker modal */}
      {blocker && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 14, padding: 26, maxWidth: 360, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 34, marginBottom: 12 }}>{blocker.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>{blocker.title}</div>
            <div style={{ color: "#7a93ab", fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>{blocker.message}</div>
            {blocker.action && (
              <button onClick={() => navigate(blocker.action.path)}
                style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>
                {blocker.action.label}
              </button>
            )}
            <button onClick={() => setBlocker(null)}
              style={{ width: "100%", padding: "11px", borderRadius: 10, border: `1px solid ${brand.line}`, background: "none", color: "#7a93ab", fontWeight: 600, cursor: "pointer" }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
